import React from "react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, X } from "lucide-react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import useCatalogueScrollRestore from "@/hooks/useCatalogueScrollRestore";

export default function Products() {
  const [products, setProducts] = useState([]);
  const [meta, setMeta] = useState({ holidays: [], occasions: [] });
  const [params, setParams] = useSearchParams();

  const occasion = params.get("occasion") || "all";
  const q = params.get("q") || "";

  // Remember where the visitor was in the grid when they clicked a product,
  // so the browser Back button drops them right back in place.
  const { saveNow } = useCatalogueScrollRestore(products.length > 0);

  useEffect(() => {
    api.get("/filters/meta").then(r => setMeta(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const s = new URLSearchParams();
    s.set("product_type", "greeting_card");
    if (occasion !== "all") s.set("occasion", occasion);
    if (q) s.set("q", q);
    api.get(`/products?${s.toString()}`).then(r => setProducts(r.data)).catch(() => {});
  }, [occasion, q]);

  // Order products the way they appear when filtered by sidebar:
  // Holidays first (in sidebar order), then Occasions (in sidebar order).
  // Within a group, preserve the underlying order returned by the API (SKU order).
  const sortedProducts = React.useMemo(() => {
    const order = [...(meta.holidays || []), ...(meta.occasions || [])];
    const idx = (o) => {
      const i = order.indexOf(o);
      return i === -1 ? 9999 : i;
    };
    return [...products].sort((a, b) => idx(a.occasion) - idx(b.occasion));
  }, [products, meta]);

  const setFilter = (k, v) => {
    const n = new URLSearchParams(params);
    if (!v || v === "all") n.delete(k); else n.set(k, v);
    setParams(n);
  };

  const clearAll = () => setParams(new URLSearchParams());
  const hasFilter = occasion !== "all" || q;
  const empty = products.length === 0;

  const FilterList = ({ label, items, testPrefix }) => (
    <div className="mt-8 first:mt-0">
      <p className="overline mb-3">{label}</p>
      <ul className="space-y-2">
        {items.map((o) => {
          const active = occasion === o;
          const testId = `filter-${testPrefix}-${o.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`;
          return (
            <li key={o}>
              <button
                data-testid={testId}
                onClick={() => setFilter("occasion", active ? "all" : o)}
                className={`text-sm text-left ${active ? "text-brand-accent font-medium" : "text-brand-dark hover:text-brand-accent"} transition-colors`}
              >
                {o}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );

  return (
    <div data-testid="products-page" className="fade-up">
      <section className="max-w-[1440px] mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-10">
        <p className="overline hidden">The Catalogue</p>
        <h1 className="font-serif font-light text-3xl sm:text-4xl md:text-5xl text-brand-dark mt-4 leading-[1.1]">
          Greeting Cards
        </h1>
        <p className="mt-4 text-brand-neutral max-w-xl">
          Filter by occasion or holiday.
        </p>
      </section>

      {/* Editorial pre-order announcement */}
      <section className="max-w-[1440px] mx-auto px-6 md:px-12">
        <aside
          data-testid="preorder-banner"
          className="border-y border-brand-border bg-[#F4F2EC] py-8 md:py-10 px-6 md:px-12 text-center"
        >
          <p className="text-[11px] tracking-[0.3em] uppercase text-brand-accent font-medium">
            Pre-Order Now Open
          </p>
          <p className="mt-3 text-brand-neutral leading-relaxed max-w-2xl mx-auto">
            All greeting cards are now available for pre-order, with delivery
            expected in early October. Pre-payment secures your order.
          </p>
        </aside>
      </section>

      <section className="max-w-[1440px] mx-auto px-6 md:px-12 pb-24">
        {/* Mobile filter — sticky select dropdown that stays under the header */}
        <div className="lg:hidden sticky top-20 z-30 bg-white/95 backdrop-blur-sm -mx-6 md:-mx-12 px-6 md:px-12 py-3 border-b border-brand-border mb-6">
          <div className="flex items-center gap-3">
            <label htmlFor="mobile-filter" className="overline text-brand-dark shrink-0">Filter</label>
            <select
              id="mobile-filter"
              data-testid="mobile-filter-select"
              value={occasion}
              onChange={(e) => setFilter("occasion", e.target.value)}
              className="flex-1 bg-transparent border-b border-brand-border py-2 text-sm text-brand-dark outline-none focus:border-brand-accent"
            >
              <option value="all">View All ({products.length})</option>
              <optgroup label="Holidays">
                {(meta.holidays || []).map(h => <option key={h} value={h}>{h}</option>)}
              </optgroup>
              <optgroup label="Occasions">
                {(meta.occasions || []).map(o => <option key={o} value={o}>{o}</option>)}
              </optgroup>
            </select>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-12">
          {/* Sidebar (desktop) — sticky, stops before footer, scrolls internally if tall */}
          <aside className="hidden lg:block lg:col-span-3 lg:sticky lg:top-24 self-start max-h-[calc(100vh-7rem)] overflow-y-auto pr-2">
            <div className="border-t border-brand-border pt-6">
              <div className="flex items-center gap-3 border-b border-brand-border pb-2 mb-6">
                <Search strokeWidth={1.5} className="w-4 h-4 text-brand-neutral" />
                <input
                  data-testid="products-search"
                  value={q}
                  onChange={(e) => setFilter("q", e.target.value)}
                  placeholder="Search"
                  className="bg-transparent flex-1 py-2 outline-none text-sm"
                />
              </div>

              <button
                data-testid="filter-view-all"
                onClick={clearAll}
                className={`overline block ${!hasFilter ? "text-brand-accent" : "text-brand-dark hover:text-brand-accent"}`}
              >
                View All
              </button>

              <FilterList label="Holidays" items={meta.holidays} testPrefix="holiday" />
              <FilterList label="Occasions" items={meta.occasions} testPrefix="occasion" />
            </div>
          </aside>

          {/* Grid */}
          <div className="lg:col-span-9">
            <div className="flex items-center justify-between mb-8 min-h-8">
              <p className="text-sm text-brand-neutral" data-testid="products-count">
                {products.length} {products.length === 1 ? "card" : "cards"}
              </p>
              {occasion !== "all" && (
                <button
                  data-testid="active-filter-chip"
                  onClick={() => setFilter("occasion", "all")}
                  className="inline-flex items-center gap-2 border border-brand-accent text-brand-accent px-3 py-1.5 text-[11px] tracking-[0.16em] uppercase hover:bg-brand-accent hover:text-white transition-colors"
                >
                  {occasion} <X strokeWidth={1.5} className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {empty ? (
              <p data-testid="products-empty" className="text-brand-neutral">
                Nothing here yet. Try clearing filters.
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-y-20 gap-x-8">
                {sortedProducts.map((p) => (
                  <ProductCard key={p.slug} product={p} overlineText={p.occasion} onNavigate={saveNow} />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
